import { Design, DesignLogo } from "../../../../core/models/entities/Design";
import { LogoType } from "../../../../core/models/entities/BaseLogo";

type PlacementZone = "LEFT" | "MIDDLE" | "RIGHT";

type PlacementMap = Map<DesignLogo["inDesignId"], string>;

interface ZoneResolution {
  zone?: PlacementZone;
  confidence: number;
}

interface LogoRect {
  logo: DesignLogo;
  left: number;
  right: number;
  width: number;
  center: number;
  top: number;
  bottom: number;
  height: number;
  centerY: number;
}

interface LogoStack {
  logos: LogoRect[];
  left: number;
  right: number;
  width: number;
  center: number;
  top: number;
  bottom: number;
}

interface ZoneBoundaries {
  leftEdge: number;
  rightEdge: number;
  width: number;
  leftBoundary: number;
  rightBoundary: number;
}

interface ViewMeta {
  ppcm?: number;
  widthPx?: number;
  locationIdentifier?: string;
}

export function getDesignLogoPlacementMap(design: Design): PlacementMap {
  const placements: PlacementMap = new Map();

  if (!design?.designLogos?.length) {
    return placements;
  }

  const style = design.style;
  const viewMeta = new Map<number, ViewMeta>();

  if (style?.styleViews?.length && style.widthInCm) {
    for (const view of style.styleViews) {
      const widthPx = view.ppcm ? style.widthInCm * view.ppcm : undefined;
      const locationIdentifier = view.view?.toUpperCase?.();
      viewMeta.set(view.id, {
        ppcm: view.ppcm,
        widthPx,
        locationIdentifier,
      });
    }
  }

  const logosByView = new Map<number, DesignLogo[]>();
  for (const logo of design.designLogos) {
    const logos = logosByView.get(logo.viewId) ?? [];
    logos.push(logo);
    logosByView.set(logo.viewId, logos);
  }

  logosByView.forEach((logos, viewId) => {
    const meta = viewMeta.get(viewId);
    const names = logos.filter(isName);
    const numbers = logos.filter(isNumber);
    if (!names.length && !numbers.length) {
      return;
    }
    const locationIdentifier = deriveLocationIdentifier(meta, names.length ? names : numbers);

    // If there's only 1 logo on this entire side, use the original viewName
    resolveAndLabelGroup(locationIdentifier, meta, names, placements, "name");
    resolveAndLabelGroup(locationIdentifier, meta, numbers, placements, "number");
  });

  return placements;
}

function calculateLogoWidthPx(logo: DesignLogo, ppcm?: number): number {
  const widthMm = logo.designerWidthMm ?? logo.widthMm;
  if (!widthMm || !ppcm) {
    return 0;
  }

  return (widthMm / 10) * ppcm;
}

function calculateLogoHeightPx(logo: DesignLogo, ppcm?: number): number {
  const heightMm = logo.heightMm;
  if (!heightMm || !ppcm) {
    return 0;
  }

  return (heightMm / 10) * ppcm;
}

function createLogoRect(logo: DesignLogo, ppcm?: number): LogoRect {
  const width = calculateLogoWidthPx(logo, ppcm);
  const rawHeight = calculateLogoHeightPx(logo, ppcm);
  const height = rawHeight > 0 ? rawHeight : width > 0 ? width * 0.6 : 1;
  const left = logo.logoX;
  const top = logo.logoY;
  const right = left + width;
  const bottom = top + height;

  return {
    logo,
    left,
    right,
    width,
    center: left + width / 2,
    top,
    bottom,
    height,
    centerY: top + height / 2,
  };
}

function deriveLocationIdentifier(
  meta: ViewMeta | undefined,
  logos: DesignLogo[]
): string {
  const fromMeta = meta?.locationIdentifier;
  if (fromMeta) {
    return fromMeta;
  }

  const fallback = logos[0]?.viewName?.toUpperCase?.();
  return fallback ?? "";
}

function createZoneResolver(
  locationIdentifier: string,
  meta: ViewMeta | undefined,
  logos: DesignLogo[],
  groupType: "name" | "number"
): (logo: DesignLogo) => ZoneResolution {
  if (!isFrontOrBack(locationIdentifier)) {
    return () => ({ zone: undefined, confidence: 0 });
  }

  const bounds = deriveZoneBounds(meta, logos);
  if (!bounds) {
    return () => ({ zone: undefined, confidence: 0 });
  }

  const rects = logos.map((logo) => createLogoRect(logo, meta?.ppcm));
  const rectByLogoId = new Map<DesignLogo["inDesignId"], LogoRect>();

  for (const rect of rects) {
    rectByLogoId.set(rect.logo.inDesignId, rect);
  }

  const stacks = buildLogoStacks(rects);
  const zoneBoundaries = computeZoneBoundaries(stacks, bounds);
  if (stacks.length === 1 && stacks[0].logos.length > 1) {
    const idSet = new Set(stacks[0].logos.map((r) => r.logo.inDesignId));
    return (logo: DesignLogo) =>
      idSet.has(logo.inDesignId)
        ? { zone: undefined, confidence: 1 }
        : { zone: undefined, confidence: 0 };
  }
  const stackAssignments = assignStacksToZones(
    stacks,
    zoneBoundaries,
    locationIdentifier,
    groupType
  );

  const fallbackResolver = (logo: DesignLogo): ZoneResolution => {
    const rect = rectByLogoId.get(logo.inDesignId);
    const fallbackZone = rect ? determineZone(rect, zoneBoundaries) : undefined;

    return {
      zone: fallbackZone,
      confidence: fallbackZone ? 0.3 : 0,
    };
  };

  return (logo: DesignLogo) =>
    stackAssignments.get(logo.inDesignId) ?? fallbackResolver(logo);
}

function deriveZoneBounds(
  meta: ViewMeta | undefined,
  logos: DesignLogo[]
): { left: number; width: number } | undefined {
  if (meta?.widthPx !== undefined) {
    return { left: 0, width: meta.widthPx };
  }

  const bounds = deriveLogoHorizontalBounds(logos, meta?.ppcm);
  if (!bounds) {
    return undefined;
  }

  return { left: bounds.left, width: bounds.right - bounds.left };
}

function deriveLogoHorizontalBounds(
  logos: DesignLogo[],
  ppcm?: number
): { left: number; right: number } | undefined {
  if (!logos.length) {
    return undefined;
  }

  let left = Number.POSITIVE_INFINITY;
  let right = Number.NEGATIVE_INFINITY;

  for (const logo of logos) {
    const logoLeft = logo.logoX;
    const logoRight = logo.logoX + calculateLogoWidthPx(logo, ppcm);

    left = Math.min(left, logoLeft);
    right = Math.max(right, logoRight);
  }

  if (!Number.isFinite(left) || !Number.isFinite(right) || right <= left) {
    return undefined;
  }

  return { left, right };
}

function determineZone(
  rect: { center: number },
  boundaries: ZoneBoundaries
): PlacementZone | undefined {
  if (!Number.isFinite(boundaries.width) || boundaries.width <= 0) {
    return undefined;
  }

  if (rect.center < boundaries.leftBoundary) {
    return "LEFT";
  }

  if (rect.center < boundaries.rightBoundary) {
    return "MIDDLE";
  }

  return "RIGHT";
}

function buildLogoStacks(rects: LogoRect[]): LogoStack[] {
  if (!rects.length) {
    return [];
  }

  const sorted = rects.slice().sort((a, b) => a.centerY - b.centerY);
  const stacks: LogoStack[] = [];

  for (const rect of sorted) {
    const currentStack = stacks[stacks.length - 1];

    if (!currentStack) {
      stacks.push(createStackFromRect(rect));
      continue;
    }

    const previousRect = currentStack.logos[currentStack.logos.length - 1];
    if (shouldStartNewStack(previousRect, rect)) {
      stacks.push(createStackFromRect(rect));
      continue;
    }

    updateStackMetrics(currentStack, rect);
  }

  return stacks;
}

function shouldStartNewStack(previous: LogoRect, candidate: LogoRect): boolean {
  const verticalGap = Math.abs(candidate.centerY - previous.centerY);
  const thresholdBase = Math.max(
    Math.max(previous.height, candidate.height),
    Math.max(previous.width, candidate.width) * 0.5,
    10
  );

  const overlap = computeHorizontalOverlap(previous, candidate);
  const minWidth = Math.min(previous.width || 1, candidate.width || 1);
  const overlapRatio = minWidth > 0 ? clamp(overlap / minWidth, 0, 1) : 0;
  const verticalAligned = verticalGap <= Math.max(previous.height, candidate.height);
  const extensionRatio = (candidate.width - overlap) / (candidate.width || 1);
  const isPair = isNameOrNumber(previous.logo) && isNameOrNumber(candidate.logo);

  // Prefer keeping Name/Number together when horizontally aligned
  if (isPair) {
    if (overlapRatio >= 0.5) return false;
    if (overlapRatio >= 0.3 && verticalAligned) return false;
  }

  if (verticalGap > thresholdBase) {
    return true;
  }
  if (overlap <= 0) {
    return true;
  }
  return extensionRatio > 0.3;
}

function createStackFromRect(rect: LogoRect): LogoStack {
  return {
    logos: [rect],
    left: rect.left,
    right: rect.right,
    width: rect.right - rect.left,
    center: rect.center,
    top: rect.top,
    bottom: rect.bottom,
  };
}

function updateStackMetrics(stack: LogoStack, rect: LogoRect): void {
  stack.logos.push(rect);
  stack.left = Math.min(stack.left, rect.left);
  stack.right = Math.max(stack.right, rect.right);
  stack.top = Math.min(stack.top, rect.top);
  stack.bottom = Math.max(stack.bottom, rect.bottom);
  stack.width = stack.right - stack.left;
  stack.center = stack.left + stack.width / 2;
}

function computeZoneBoundaries(
  stacks: LogoStack[],
  bounds: { left: number; width: number }
): ZoneBoundaries {
  const leftEdge = bounds.left;
  const width = bounds.width;
  const rightEdge = leftEdge + width;

  if (!Number.isFinite(width) || width <= 0) {
    return {
      leftEdge,
      rightEdge,
      width,
      leftBoundary: leftEdge,
      rightBoundary: rightEdge,
    };
  }

  const sorted = stacks.slice().sort((a, b) => a.center - b.center);
  const boundaries: number[] = [];

  for (
    let index = 0;
    index < sorted.length - 1 && boundaries.length < 2;
    index++
  ) {
    const current = sorted[index];
    const next = sorted[index + 1];
    const gapLeft = Math.max(current.right, current.center);
    const gapRight = Math.min(next.left, next.center);
    const midpoint = (gapLeft + gapRight) / 2;

    if (Number.isFinite(midpoint)) {
      boundaries.push(midpoint);
    }
  }

  if (boundaries.length === 1) {
    const first = boundaries[0];
    const remainder = rightEdge - first;
    boundaries.push(first + remainder / 2);
  }

  while (boundaries.length < 2) {
    boundaries.push(leftEdge + ((boundaries.length + 1) * width) / 3);
  }

  const leftBoundary = clamp(boundaries[0], leftEdge, rightEdge);
  const rightBoundary = clamp(boundaries[1], leftEdge, rightEdge);

  return {
    leftEdge,
    rightEdge,
    width,
    leftBoundary: Math.min(leftBoundary, rightBoundary),
    rightBoundary: Math.max(leftBoundary, rightBoundary),
  };
}

function assignStacksToZones(
  stacks: LogoStack[],
  boundaries: ZoneBoundaries,
  locationIdentifier: string,
  groupType: "name" | "number"
): Map<DesignLogo["inDesignId"], ZoneResolution> {
  const assignments = new Map<DesignLogo["inDesignId"], ZoneResolution>();

  for (const stack of stacks) {
    const zone = determineZone(stack, boundaries);
    const positionScore = zone
      ? computePositionScore(stack.center, zone, boundaries)
      : 0;
    const clusteringScore = computeClusteringScore(stack);
    let baseConfidence = zone
      ? clamp(0.6 * positionScore + 0.4 * clusteringScore, 0, 1)
      : 0;
    baseConfidence = applyLocationBias(
      baseConfidence,
      zone,
      locationIdentifier,
      stack,
      groupType
    );

    for (const rect of stack.logos) {
      const penalty = computeAlignmentPenalty(rect, stack);
      const confidence = zone
        ? clamp(baseConfidence * (1 - penalty), 0.1, 1)
        : 0;
      assignments.set(rect.logo.inDesignId, { zone, confidence });
    }
  }

  return assignments;
}

function computePositionScore(
  center: number,
  zone: PlacementZone,
  boundaries: ZoneBoundaries
): number {
  if (!Number.isFinite(boundaries.width) || boundaries.width <= 0) {
    return 0.5;
  }

  const width = boundaries.width;

  if (zone === "LEFT") {
    const distance = Math.max(0, boundaries.leftBoundary - center);
    return clamp(distance / (width / 3), 0, 1);
  }

  if (zone === "RIGHT") {
    const distance = Math.max(0, center - boundaries.rightBoundary);
    return clamp(distance / (width / 3), 0, 1);
  }

  const middle = (boundaries.leftBoundary + boundaries.rightBoundary) / 2;
  const halfSpan = Math.max(
    1,
    (boundaries.rightBoundary - boundaries.leftBoundary) / 2
  );
  const distanceToMiddle = Math.abs(center - middle);
  return clamp(1 - distanceToMiddle / halfSpan, 0, 1);
}

function computeClusteringScore(stack: LogoStack): number {
  if (stack.logos.length <= 1) {
    return 0.6;
  }

  const sorted = stack.logos.slice().sort((a, b) => a.centerY - b.centerY);
  let totalOverlapRatio = 0;
  let comparisons = 0;
  let bonus = 0;

  for (let index = 0; index < sorted.length - 1; index++) {
    const current = sorted[index];
    const next = sorted[index + 1];
    const overlap = computeHorizontalOverlap(current, next);
    const minWidth = Math.min(current.width || 1, next.width || 1);

    if (minWidth > 0) {
      totalOverlapRatio += clamp(overlap / minWidth, 0, 1);
      comparisons += 1;

      const ratio = clamp(overlap / minWidth, 0, 1);
      const isNameOrNumberPair =
        isNameOrNumber(current.logo) && isNameOrNumber(next.logo);
      const verticalAligned =
        Math.abs(current.centerY - next.centerY) <=
        Math.max((current.height + next.height) / 4, 5);

      if (isNameOrNumberPair && verticalAligned) {
        if (ratio >= 0.5) {
          bonus += 0.2;
        } else if (ratio >= 0.3) {
          bonus += 0.1;
        }
      }
    }
  }

  if (!comparisons) {
    return 0.7;
  }

  const averageOverlap = totalOverlapRatio / comparisons;
  return clamp(0.5 + averageOverlap / 2 + bonus, 0, 1);
}

function computeAlignmentPenalty(rect: LogoRect, stack: LogoStack): number {
  if (stack.logos.length <= 1) {
    return 0;
  }

  let bestOverlapRatio = 0;

  for (const other of stack.logos) {
    if (other === rect) {
      continue;
    }

    const overlap = computeHorizontalOverlap(rect, other);
    const minWidth = Math.min(rect.width || 1, other.width || 1);

    if (minWidth > 0) {
      const ratio = clamp(overlap / minWidth, 0, 1);
      bestOverlapRatio = Math.max(bestOverlapRatio, ratio);
    }
  }

  return clamp(0.5 * (1 - bestOverlapRatio), 0, 0.5);
}

function computeHorizontalOverlap(a: LogoRect, b: LogoRect): number {
  return Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left));
}

function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) {
    return min;
  }

  return Math.max(min, Math.min(max, value));
}

function isNameOrNumber(logo: DesignLogo): boolean {
  const type = logo.logo?.logoType;
  return type === LogoType.NameLogo || type === LogoType.NumberLogo;
}

function stackContainsNumber(stack: LogoStack): boolean {
  return stack.logos.some((r) => r.logo.logo?.logoType === LogoType.NumberLogo);
}

function isName(logo: DesignLogo): boolean {
  return logo.logo?.logoType === LogoType.NameLogo;
}

function isNumber(logo: DesignLogo): boolean {
  return logo.logo?.logoType === LogoType.NumberLogo;
}

function applyLocationBias(
  confidence: number,
  zone: PlacementZone | undefined,
  locationIdentifier: string,
  stack: LogoStack,
  groupType?: "name" | "number"
): number {
  if (!zone) return confidence;
  const loc = locationIdentifier?.toUpperCase?.() ?? "";
  if (loc === "BACK") {
    if (zone === "MIDDLE") return clamp(confidence * 1.3, 0, 1);
    return clamp(confidence * 0.85, 0, 1);
  }
  if (loc === "FRONT") {
    if (groupType === "number") return confidence;
    if (zone === "MIDDLE") return clamp(confidence * 0.75, 0, 1);
    return clamp(confidence * 1.15, 0, 1);
  }
  return confidence;
}

function logZoneResolution(
  logo: DesignLogo,
  resolution: ZoneResolution,
  locationIdentifier: string,
  label: string
): void {
  if (typeof console?.debug !== "function") {
    return;
  }

  console.debug("design-logo-zone", {
    inDesignId: logo.inDesignId,
    zone: resolution.zone ?? "UNRESOLVED",
    confidence: Number(resolution.confidence.toFixed(2)),
    location: locationIdentifier,
    label,
  });
}

function buildBaseLabel(
  locationIdentifier: string,
  zone?: PlacementZone
): string {
  const normalizedLocation = locationIdentifier?.toUpperCase?.() ?? "";

  if (!normalizedLocation) {
    return "";
  }

  if (normalizedLocation === "LEFT" || normalizedLocation === "RIGHT") {
    return `${normalizedLocation} SIDE`.trim();
  }

  if (isFrontOrBack(normalizedLocation)) {
    if (!zone) {
      return normalizedLocation;
    }

    if (zone === "MIDDLE") {
      return `${normalizedLocation} MIDDLE`.trim();
    }

    return `${normalizedLocation} ${zone}`.trim();
  }

  if (!zone || zone === "MIDDLE") {
    return normalizedLocation;
  }

  return `${normalizedLocation} ${zone}`.trim();
}

function applyFrontPerspectiveSwap(
  label: string,
  locationIdentifier: string
): string {
  if (locationIdentifier !== "FRONT") {
    return label;
  }

  return label.replace(/\b(LEFT|RIGHT)\b/g, (match) =>
    match === "LEFT" ? "RIGHT" : "LEFT"
  );
}

function isFrontOrBack(locationIdentifier: string): boolean {
  return locationIdentifier === "FRONT" || locationIdentifier === "BACK";
}
function resolveAndLabelGroup(
  locationIdentifier: string,
  meta: ViewMeta | undefined,
  group: DesignLogo[],
  placements: PlacementMap,
  groupType: "name" | "number"
): void {
  if (!group.length) return;

  if (group.length === 1) {
    const originalViewName = group[0]?.viewName?.toUpperCase?.() ?? "";
    const finalLabel = applyFrontPerspectiveSwap(
      originalViewName,
      locationIdentifier
    );
    placements.set(group[0].inDesignId, finalLabel);
    return;
  }

  const stacks = buildLogoStacks(group.map((logo) => createLogoRect(logo, meta?.ppcm)));
  if (stacks.length === 1 && stacks[0].logos.length > 1) {
    const sorted = stacks[0].logos
      .map((r) => r.logo)
      .slice()
      .sort((a, b) => a.logoY - b.logoY);
    sorted.forEach((logo, index) => {
      const numberedLabel = `${locationIdentifier} ${index + 1}`.trim();
      const finalLabel = applyFrontPerspectiveSwap(numberedLabel, locationIdentifier);
      placements.set(logo.inDesignId, finalLabel);
    });
    return;
  }

  const zoneResolver = createZoneResolver(locationIdentifier, meta, group, groupType);
  const resolutionByLogo = new Map<DesignLogo["inDesignId"], ZoneResolution>();
  const groupedByLabel = new Map<string, DesignLogo[]>();

  for (const logo of group) {
    const resolution = zoneResolver(logo);
    resolutionByLogo.set(logo.inDesignId, resolution);
    const baseLabel = buildBaseLabel(locationIdentifier, resolution.zone);
    const entries = groupedByLabel.get(baseLabel) ?? [];
    entries.push(logo);
    groupedByLabel.set(baseLabel, entries);
  }

  groupedByLabel.forEach((groupLogos, label) => {
    const sorted = groupLogos.slice().sort((a, b) => a.logoY - b.logoY);
    if (sorted.length > 1) {
      sorted.forEach((logo, index) => {
        const numberedLabel = `${label} ${index + 1}`.trim();
        const finalLabel = applyFrontPerspectiveSwap(numberedLabel, locationIdentifier);
        placements.set(logo.inDesignId, finalLabel);
        const resolution = resolutionByLogo.get(logo.inDesignId);
        if (resolution) {
          logZoneResolution(logo, resolution, locationIdentifier, finalLabel);
        }
      });
      return;
    }
    const finalLabel = applyFrontPerspectiveSwap(label, locationIdentifier);
    placements.set(groupLogos[0].inDesignId, finalLabel);
    const resolution = resolutionByLogo.get(groupLogos[0].inDesignId);
    if (resolution) {
      logZoneResolution(groupLogos[0], resolution, locationIdentifier, finalLabel);
    }
  });
}
